import React, { createContext, useContext, useState, useEffect } from "react";
const CryptoJS = require("crypto-js");

/**
 * Contexto das Informações do Vendedor como nome, catalog Id, desconto , etc
 * 
 * Este contexto gerencia os dados do vendedor que o usuário pode acessar.
 */

type Seller = {
    cpf: string;
    email: string;
    id: number;
    name: string;
    numberPhone: string;
    pluginspaceId: number;
    sellerCode: string | null;
    image?: string;
}

type SellerInfoType = {
    seller: Seller;
    catalogId: number;
    cpfVendorByLink: string
    updateCustomerCart: boolean;
    discountValue: number;
}


type SellerInfoContextType = {
    data: SellerInfoType;
    setSellerInfoData: (infoData: SellerInfoType) => void;
    setCatalogId: (catalogId: number) => void;
    setCpfVendorByLink: (cpfVendorByLink: string) => void;
    setUpdateCustomerCart: (updateCustomerCart: boolean) => void;
    setDiscountValue: (discountValue: number) => void;
    clearSellerInfo: () => void;
    setSellerInfo: (seller: Seller) => void;
    sellerInfo: Seller;
    catalogId: number;
    discountValue: number;
    cpfVendorByLink: string;
    updateCustomerCart: boolean;
};



const initialData: SellerInfoType = {
    seller: {
        cpf: "",
        email: "",
        id: 0,
        name: "",
        numberPhone: "",
        pluginspaceId: 0,
        sellerCode: "",
        image: ""
    },
    catalogId: 0,
    cpfVendorByLink: "",
    updateCustomerCart: false,
    discountValue: 0
};
 
const STORAGE_KEY = "sellerInfoData";
const SECRET = !process.env.REACT_APP_JWT_SALT ? "ninegridCrypoToken" : process.env.REACT_APP_JWT_SALT;





export const SellerInfoContext = createContext<SellerInfoContextType>({} as SellerInfoContextType);


export const SellerInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [data, setData] = useState<SellerInfoType>(initialData);

    // Carrega do localStorage ao iniciar
    useEffect(() => {
        const encrypted = localStorage.getItem(STORAGE_KEY);
        if (encrypted) {
            try {
                const bytes = CryptoJS.AES.decrypt(encrypted, SECRET);
                const decrypted = bytes.toString(CryptoJS.enc.Utf8);
                if (decrypted) setData(JSON.parse(decrypted));
            } catch {
                setData(initialData);
            }
        }
    }, []);

    // Salva no localStorage sempre que mudar
    useEffect(() => {
        const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), SECRET).toString();
        localStorage.setItem(STORAGE_KEY, encrypted);
         console.log("SellerInfoContext data saved to localStorage:", data); // Log para depuração
    }, [data]);

    const setSellerInfoData = (infoData: SellerInfoType) => {
        setData(infoData);
    };

    const setSellerInfo = (seller: Seller) => {
        setData((prev) => ({ ...prev, seller }));
    }

    const setCatalogId = (catalogId: number) => {
        setData((prev) => ({ ...prev, catalogId }));
    };

    const setCpfVendorByLink = (cpfVendorByLink: string) => {
        setData((prev) => ({ ...prev, cpfVendorByLink }));
    };

    const setUpdateCustomerCart = (updateCustomerCart: boolean) => {
        setData((prev) => ({ ...prev, updateCustomerCart }));
    };

    const setDiscountValue = (discountValue: number) => {
        setData((prev) => ({ ...prev, discountValue }));
    };
 
    const clearSellerInfo = () => {
        setData(initialData);
        localStorage.removeItem(STORAGE_KEY);
    };

    const sellerInfo = data.seller;
    const catalogId = data.catalogId;
    const discountValue = data.discountValue;
    const cpfVendorByLink = data.cpfVendorByLink;
    const updateCustomerCart = data.updateCustomerCart;


    return (
        <SellerInfoContext.Provider 
            value={{ 
                data, 
                setSellerInfoData, 
                setCatalogId, 
                setCpfVendorByLink, 
                setUpdateCustomerCart, 
                setDiscountValue, 
                clearSellerInfo,
                setSellerInfo,
                sellerInfo,
                catalogId,
                discountValue,
                cpfVendorByLink,
                updateCustomerCart
            }}
        >
            {children}
        </SellerInfoContext.Provider>
    );
};


export const useSellerInfo = () => {
    const context = useContext(SellerInfoContext);
    if (context === undefined) {
        throw new Error('useSellerInfo must be used within a SellerCartProvider');
    }
    return context;
};